<script setup lang="ts">
    import { ref, computed, watch } from 'vue';
    import type { FileItem } from '@/types';
    import { Check, Eye, File, FileUp, FileWarning, FileX, Trash2, Upload } from 'lucide-vue-next';
    import { useI18n } from 'vue-i18n';
    import { Button } from '@/components/ui/button';
    import { popupCenterDual } from '@/utils/helpers';
    import axios from 'axios';

    const { t } = useI18n();

    interface Emits {
        (e: 'files-processing', processing: boolean): void;
        (e: 'update:modelValue', value: FileItem[]): void;
        (e: 'success', message: string): void;
        (e: 'update:removedFileIds', value: number[]): void;
    }

    const props = withDefaults(
        defineProps<{
            modelValue?: FileItem[];
            removedFileIds?: number[];
            route?: string;
            acceptsFiles?: string[];
            multiple?: boolean;
            maxUploads?: number;
            maxSize?: number;
            showPreview?: boolean;
            additionalFields?: Record<string, any>;
            concurrency?: number;
            instantUpload?: boolean;
            batchUpload?: boolean;
            deleteFile?: (payload: { id: number }) => Promise<void>;
        }>(),
        {
            modelValue: () => [],
            removedFileIds: () => [],
            route: '/',
            acceptsFiles: () => [],
            multiple: false,
            maxUploads: 10,
            maxSize: 10 * 1024 * 1024, // 10MB
            showPreview: false,
            additionalFields: () => ({}),
            concurrency: 2,
            instantUpload: false,
            batchUpload: false,
        },
    );

    const emit = defineEmits<Emits>();

    const dropzoneRef = ref<HTMLElement>();
    const fileInputRef = ref<HTMLInputElement>();
    const files = ref<FileItem[]>([...(props.modelValue ?? [])]);
    const removedFileIds = ref<number[]>([...(props.removedFileIds ?? [])]);
    const isDragActive = ref(false);
    const hasError = ref(false);
    const errorMessage = ref('');
    const successMessage = ref('');
    const dragCounter = ref(0);

    const uploadedFiles = computed(() => {
        if (props.instantUpload) {
            return files.value.filter((f) => f.uploaded);
        } else {
            return files.value.filter((f) => !f.error && !f.uploading);
        }
    });
    const circumference = computed(() => 2 * Math.PI * 10); // 2πr where r=10

    const strokeDashoffset = (percentage: number) => {
        return circumference.value - (percentage / 100) * circumference.value;
    };

    const openFileDialog = () => {
        fileInputRef.value?.click();
    };

    const handleFileSelect = (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (target.files) {
            handleFiles(Array.from(target.files));
        }
    };

    const handleDrop = (event: DragEvent) => {
        event.preventDefault();
        isDragActive.value = false;
        dragCounter.value = 0;

        if (event.dataTransfer?.files) {
            handleFiles(Array.from(event.dataTransfer.files));
        }
    };

    const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
    };

    const handleDragEnter = (event: DragEvent) => {
        event.preventDefault();
        dragCounter.value++;
        isDragActive.value = true;
    };

    const handleDragLeave = (event: DragEvent) => {
        event.preventDefault();
        dragCounter.value--;
        if (dragCounter.value === 0) {
            isDragActive.value = false;
        }
    };

    const validateFile = (file: File): string | null => {
        // Check file size
        if (props.maxSize && file.size > props.maxSize) {
            return t('upload.file_size_exceeds', {
                max_size: formatFileSize(props.maxSize),
            });
        }

        // Check file type
        if (props.acceptsFiles && props.acceptsFiles.length > 0) {
            const fileType = file.type;
            const fileName = file.name.toLowerCase();

            const isAccepted = props.acceptsFiles.some((acceptedType) => {
                if (acceptedType.startsWith('.')) {
                    return fileName.endsWith(acceptedType.toLowerCase());
                }
                if (acceptedType.includes('*')) {
                    const baseType = acceptedType.split('/')[0];
                    return fileType.startsWith(baseType);
                }
                return fileType === acceptedType;
            });

            if (!isAccepted) {
                return t('upload.file_type_not_accepted', {
                    allowed: props.acceptsFiles.join(', '),
                });
            }
        }

        return null;
    };

    const createFileItem = (file: File): FileItem => {
        const fileItem: FileItem = {
            id: Date.now() + Math.random(),
            file,
            name: file.name,
            size: file.size,
            uploading: false,
            uploaded: false,
            progress: 0,
            registered: false,
            mime: file.type,
        };

        // Create preview for images
        // if (props.showPreview && file.type.startsWith('image/')) {
        //     const reader = new FileReader();
        //     reader.onload = (e) => {
        //         fileItem.preview = e.target?.result as string;
        //     };
        //     reader.readAsDataURL(file);
        // }

        return fileItem;
    };

    const hasXsrfCookie = (): boolean => {
        return document.cookie.split('; ').some((row) => row.startsWith('XSRF-TOKEN='));
    };

    const refreshXsrfCookieIfMissing = async (): Promise<void> => {
        if (hasXsrfCookie()) {
            return;
        }

        try {
            await axios.get(window.location.pathname, {
                withCredentials: true,
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
        } catch (_) {
            // swallow; cookie may still be present afterwards
        }
    };

    const uploadWithConcurrency = async (files: FileItem[], concurrency: number) => {
        const results: Promise<void>[] = [];
        const running: Promise<void>[] = [];

        for (const file of files) {
            const promise = uploadFile(file).finally(() => {
                // Remove from running when done
                const index = running.indexOf(promise);
                if (index > -1) {
                    running.splice(index, 1);
                }
            });

            results.push(promise);
            running.push(promise);

            // If we've reached the concurrency limit, wait for one to finish
            if (running.length >= concurrency) {
                await Promise.race(running);
            }
        }

        // Wait for all remaining uploads to complete
        await Promise.all(results);
    };

    const uploadFilesInBatch = async (fileItems: FileItem[]): Promise<void> => {
        const formData = new FormData();

        fileItems.forEach((item) => {
            formData.append(`file[]`, item.file as File);
        });

        if (props.additionalFields) {
            Object.entries(props.additionalFields).forEach(([key, value]) => {
                formData.append(key, String(value));
            });
        }

        // Set uploading state and initialize progress
        fileItems.forEach((item) => {
            updateFileState(item.id, { uploading: true });
            uploadProgress.value[item.id] = 0;
        });

        await refreshXsrfCookieIfMissing();

        const response = await axios.post(props.route as string, formData, {
            withCredentials: true,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                Accept: 'application/json',
            },
            onUploadProgress: (event) => {
                if (event.total) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    fileItems.forEach((item) => {
                        uploadProgress.value[item.id] = progress;
                    });
                }
            },
        });

        fileItems.forEach((item, idx) => {
            updateFileState(item.id, {
                uploaded: true,
                uploading: false,
                error: undefined,
                url: (response.data?.files?.data?.[idx]?.url) as string | undefined,
                voucher_statement_id: response.data?.files?.voucher_statement_id,
            });
            uploadProgress.value[item.id] = 100;
        });
    };

    const handleFiles = async (newFiles: File[]) => {
        clearError();

        // Check max uploads limit
        const totalFiles = files.value.length + newFiles.length;
        if (props.maxUploads && totalFiles > props.maxUploads) {
            showError(t('upload.max_uploads_exceeded', { max_uploads: props.maxUploads }));
            return;
        }

        // Validate and create file items
        const validFiles: FileItem[] = [];

        for (const file of newFiles) {
            const error = validateFile(file);
            if (error) {
                showError(error);
                continue;
            }

            // Check for duplicates
            const isDuplicate = files.value.some(
                (f) => f.name === file.name && f.size === file.size,
            );

            if (isDuplicate) {
                showError(t('upload.file_already_added', { file_name: file.name }));
                continue;
            }

            validFiles.push(createFileItem(file));
        }

        if (validFiles.length === 0) return;

        files.value.push(...validFiles);

        // Only upload immediately if instantUpload is true
        if (props.instantUpload) {
            try {
                if (props.batchUpload) {
                    await uploadFilesInBatch(validFiles);
                } else {
                    await uploadWithConcurrency(validFiles, props.concurrency);
                }
                const allSuccess = files.value.every((f) => f.uploaded && !f.error);
                if (allSuccess) {
                    emit('success', successMessage.value);
                }
            } catch (error) {
                showError(t('upload.upload_failed', { status_text: error }));
                console.error(error);
            }
        }

        emitUploadedFiles();
    };

    const uploadProgress = ref<Record<string, number>>({});

    const updateFileState = (fileId: number, updates: Partial<FileItem>) => {
        const fileIndex = files.value.findIndex((f) => f.id === fileId);
        if (fileIndex !== -1) {
            files.value[fileIndex] = { ...files.value[fileIndex], ...updates };
            emit(
                'files-processing',
                files.value.some((f) => f.uploading),
            );
        } else {
            console.warn(`File ${fileId} not found in files array`);
        }
    };

    const uploadFile = async (fileItem: FileItem): Promise<void> => {
        if (!props.route) {
            updateFileState(fileItem.id, { error: t('upload.no_upload_route_specified') });
            return;
        }

        // Clear any previous errors and set uploading state
        updateFileState(fileItem.id, {
            uploading: true,
            uploaded: false,
            error: undefined,
        });
        uploadProgress.value[fileItem.id] = 0;

        const formData = new FormData();
        formData.append('file', fileItem.file as File);

        // Add additional fields to the form data
        if (props.additionalFields) {
            Object.entries(props.additionalFields).forEach(([key, value]) => {
                formData.append(key, String(value));
            });
        }

        try {
            await refreshXsrfCookieIfMissing();

            const response = await axios.post(props.route as string, formData, {
                withCredentials: true,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/json',
                },
                onUploadProgress: (event) => {
                    if (event.total) {
                        uploadProgress.value[fileItem.id] = Math.round(
                            (event.loaded / event.total) * 100,
                        );
                    }
                },
            });

            updateFileState(fileItem.id, {
                uploaded: true,
                uploading: false,
                error: undefined,
                url: response.data?.files?.data?.[0]?.url,
                voucher_statement_id: response.data?.files?.voucher_statement_id,
            });
            uploadProgress.value[fileItem.id] = 100;
        } catch (error: any) {
            const statusText = error?.response?.data?.message || error?.message || 'Upload failed';
            updateFileState(fileItem.id, {
                error: statusText,
                uploading: false,
                uploaded: false,
            });
        }
    };

    const removeFile = async (index: number) => {
        const fileItem = files.value[index];
        if (fileItem.uploading) {
            return;
        }

        if ((fileItem.uploaded || fileItem.registered) && props.deleteFile) {
            try {
                await props.deleteFile({
                    id: fileItem.id,
                });
            } catch (error) {
                showError(t('upload.delete_failed'));

                return;
            }
        }

        if (fileItem.registered && typeof fileItem.id === 'number') {
            if (!removedFileIds.value.includes(fileItem.id)) {
                removedFileIds.value.push(fileItem.id);
            }

            emit('update:removedFileIds', [...removedFileIds.value]);
        }

        files.value.splice(index, 1);
        emitUploadedFiles();
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const showError = (message: string) => {
        hasError.value = true;
        errorMessage.value = message;
        setTimeout(clearError, 5000);
    };

    const clearError = () => {
        hasError.value = false;
        errorMessage.value = '';
    };

    const emitUploadedFiles = () => {
        const value = uploadedFiles.value;
        emit('update:modelValue', value);
    };

    const openFile = (file: FileItem) => {
        if (file.url && (file.mime?.startsWith('image/') || file.mime === 'application/pdf')) {
            popupCenterDual(file.url, 'preview', window.innerWidth / 2, window.innerHeight / 1.1);
        } else {
            window.open(file.url, '_blank');
        }
    };

    const markFilesAsRegistered = () => {
        uploadedFiles.value.forEach((file) => {
            file.registered = true;
        });
    };

    const uploadAllFiles = async () => {
        const unuploadedFiles = files.value.filter((f) => !f.uploaded && !f.uploading && !f.error);

        if (unuploadedFiles.length === 0) {
            return;
        }

        try {
            if (props.batchUpload) {
                await uploadFilesInBatch(unuploadedFiles);
            } else {
                await uploadWithConcurrency(unuploadedFiles, props.concurrency);
            }
        } catch (error) {
            showError(t('upload.upload_failed', { status_text: error }));
            console.error(error);
        }

        emitUploadedFiles();
    };

    watch(
        () => props.modelValue,
        (newVal) => {
            if (newVal) {
                files.value = [...newVal];
            }
        },
    );

    watch(
        () => props.removedFileIds,
        (newVal) => {
            removedFileIds.value = [...(newVal ?? [])];
        },
    );

    defineExpose({
        files: files.value,
        markFilesAsRegistered,
        uploadAllFiles,
    });
</script>

<template>
    <div
        ref="dropzoneRef"
        class="bg-faded text-muted-foreground flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border p-4 text-xs transition-all"
        :class="{
            'border-primary/50 bg-primary/10': isDragActive,
            'border-destructive/50 bg-destructive/10': hasError,
        }"
        @drop="handleDrop"
        @dragover="handleDragOver"
        @dragenter="handleDragEnter"
        @dragleave="handleDragLeave"
        @click="openFileDialog"
    >
        <input
            ref="fileInputRef"
            type="file"
            :accept="acceptsFiles.join(',')"
            :multiple="multiple"
            @change="handleFileSelect"
            class="hidden"
        />

        <Upload class="size-4" />
        <span>{{ t('upload.content') }}</span>

        <div
            v-if="hasError"
            class="bg-destructive/10 text-destructive flex w-full cursor-default items-center gap-2 rounded-md p-2 text-xs"
        >
            <FileWarning class="size-4 shrink-0" />
            {{ errorMessage }}
        </div>

        <div
            v-if="files.length > 0"
            class="mt-2 flex w-full flex-col gap-2"
        >
            <div
                v-for="(file, index) in files"
                :key="file.id"
                class="flex w-full cursor-default items-center gap-2 rounded-md border p-2 text-xs transition-all"
                @click.stop
                :class="{
                    'bg-white': !file.uploading && !file.error && !file.uploaded,
                    'border-primary/50 bg-primary/10': file.uploading,
                    'border-destructive/50 bg-destructive/10': file.error,
                    'border-accent/50 bg-accent/10': file.uploaded,
                }"
            >
                <div class="flex w-8 items-center justify-center">
                    <File
                        v-if="file.registered"
                        class="text-muted-foreground size-4"
                    />
                    <FileUp
                        v-if="!file.registered && !instantUpload"
                        class="text-muted-foreground size-4"
                    />
                    <svg
                        v-if="instantUpload && !file.uploaded && !file.error"
                        class="rotate-[-90deg]"
                        width="32"
                        height="32"
                    >
                        <circle
                            class="stroke-border"
                            stroke-width="2"
                            fill="transparent"
                            r="10"
                            cx="16"
                            cy="16"
                        />
                        <circle
                            class="stroke-primary stroke-linecap-round transition-stroke-dashoffset duration-300 ease-in-out"
                            stroke-width="2"
                            fill="transparent"
                            r="10"
                            cx="16"
                            cy="16"
                            :stroke-dasharray="circumference"
                            :stroke-dashoffset="strokeDashoffset(uploadProgress[file.id] || 0)"
                        />
                    </svg>
                    <Check
                        v-if="!file.registered && file.uploaded"
                        class="size-4"
                    />
                    <FileX
                        v-if="file.error"
                        class="size-4"
                    />
                </div>

                <div class="flex min-w-0 flex-1 flex-col gap-1">
                    <div class="text-foreground w-full truncate font-medium">{{ file.name }}</div>
                    <div
                        v-if="file.size || file.uploading"
                        class="text-muted-foreground text-[10px]"
                    >
                        <span v-if="file.uploading"> {{ uploadProgress[file.id] || 0 }}% - </span>
                        <span v-if="file.size"> {{ formatFileSize(file.size) }} </span>
                    </div>
                    <div
                        v-if="file.error"
                        class="text-destructive text-xs"
                    >
                        {{ file.error }}
                    </div>
                </div>

                <div class="flex items-center">
                    <Button
                        v-if="file.url"
                        variant="ghost_primary"
                        size="icon"
                        :disabled="file.uploading"
                        @click.prevent="openFile(file)"
                    >
                        <Eye class="size-4" />
                    </Button>

                    <Button
                        variant="ghost_destructive"
                        size="icon"
                        @click.prevent="removeFile(index)"
                        :disabled="file.uploading"
                    >
                        <Trash2 class="size-4" />
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
export default {};
</script>
